/**
 * LooksGoodCard — reassurance block shown after the user has entered enough
 * data ("contract may qualify" + landlord context).
 *
 * Heyflow widget tag:
 *   <div data-conny-widget="looks-good-card"></div>
 *
 * Inputs (all optional — the card degrades gracefully):
 *   - Address fields (street / house / zip) — same aliases as the address
 *     card. Used to auto-detect the landlord via the funnelboat API.
 *   - Landlord selection — usually set by the landlord-card earlier in the
 *     funnel via these sessionStorage keys:
 *         conny_landlord_known_key
 *         conny_landlord_type
 *
 * If neither a detected nor a user-selected landlord is available, the
 * landlord reassurance block is hidden and only the generic "contract may
 * qualify" / "enough data for first indication" cards are shown.
 */
import { useQuery } from "@tanstack/react-query";
import { getFunnelContext, getLandlord, Landlord } from "../../shared/api";
import { readAddress } from "../../shared/address";
import { AnimatedNumber } from "../../shared/AnimatedNumber";
import {
  CheckIcon,
  CircleCheckIcon,
  FileIcon,
  ShieldIcon,
} from "../../shared/icons";
import { STORAGE_KEYS, useStorage } from "../../shared/storage";

interface Resolved {
  landlord: Landlord | null;
  type: string;
}

function useResolvedLandlord(): Resolved | null {
  const [knownKey] = useStorage(STORAGE_KEYS.landlordKnownKey);
  const [landlordType] = useStorage(STORAGE_KEYS.landlordType);
  const [street] = useStorage(STORAGE_KEYS.street);
  const [house] = useStorage(STORAGE_KEYS.house);
  const [zip] = useStorage(STORAGE_KEYS.zip);

  const explicit = useQuery({
    queryKey: ["landlord", knownKey],
    queryFn: () => getLandlord(knownKey),
    enabled: Boolean(knownKey && knownKey !== "other_commercial"),
    staleTime: 30 * 60_000,
  });

  const addr = readAddress();
  const fromAddr = street && house && zip ? addr : null;
  const ctx = useQuery({
    queryKey: [
      "funnel-context",
      fromAddr?.street,
      fromAddr?.houseNumber,
      fromAddr?.zip,
    ],
    queryFn: () =>
      getFunnelContext({
        street: fromAddr!.street,
        house: fromAddr!.houseNumber,
        zip: fromAddr!.zip,
      }),
    enabled: Boolean(fromAddr),
    staleTime: 5 * 60_000,
  });

  if (explicit.data)
    return { landlord: explicit.data, type: "known_corporate" };
  if (ctx.data?.landlord)
    return {
      landlord: ctx.data.landlord,
      type: ctx.data.landlord.type || "known_corporate",
    };
  if (landlordType === "private" || landlordType === "other_corporate") {
    return { landlord: null, type: landlordType };
  }
  return null;
}

export function LooksGoodCard() {
  const resolved = useResolvedLandlord();

  return (
    <div className="cn">
      {resolved && <LandlordReassurance resolved={resolved} />}

      <details className="cn-card">
        <summary style={{ listStyle: "none", cursor: "pointer" }}>
          <div className="cn-top">
            <div className="cn-icon">
              <CircleCheckIcon />
            </div>
            <div
              className="cn-head"
              style={{ display: "flex", alignItems: "center", gap: 10 }}
            >
              <span style={{ flex: 1 }}>Contract may qualify</span>
              <span className="cn-chip">Likely</span>
            </div>
          </div>
        </summary>
        <div className="cn-body">
          <Bullet>Move-in date is after 1&nbsp;June&nbsp;2015</Bullet>
          <Bullet>
            Rent control applies to both furnished and unfurnished apartments
          </Bullet>
        </div>
      </details>

      <details className="cn-card">
        <summary style={{ listStyle: "none", cursor: "pointer" }}>
          <div className="cn-top">
            <div className="cn-icon">
              <FileIcon />
            </div>
            <div
              className="cn-head"
              style={{ display: "flex", alignItems: "center", gap: 10 }}
            >
              <span style={{ flex: 1 }}>Enough data for first indication</span>
              <span className="cn-chip">Ready</span>
            </div>
          </div>
        </summary>
        <div className="cn-body">
          <CheckLine>
            We can now calculate an initial savings indication based on your
            rent and apartment details.
          </CheckLine>
        </div>
      </details>
    </div>
  );
}

function LandlordReassurance({ resolved }: { resolved: Resolved }) {
  const { landlord, type } = resolved;
  let name: string;
  let chip: React.ReactNode;
  let point1: React.ReactNode;
  let point2: string;
  let point3: string;

  if (landlord && landlord.count) {
    name = landlord.name;
    chip = (
      <>
        <AnimatedNumber value={landlord.count} />
        &nbsp;cases
      </>
    );
    point1 = (
      <>
        Conny has already handled <AnimatedNumber value={landlord.count} />{" "}
        requests with {landlord.name}.
      </>
    );
    point2 =
      "Large landlords usually process these cases through standard legal or admin teams.";
    point3 =
      "They cannot simply end your tenancy because you check or enforce your rights.";
  } else if (type === "other_corporate") {
    name = "Corporate landlord";
    chip = "Common";
    point1 =
      "Corporate landlords are familiar with rent reviews and legal checks.";
    point2 =
      "These cases are usually handled by administration or legal teams, not personally.";
    point3 =
      "They cannot simply end your tenancy because you check or enforce your rights.";
  } else if (type === "private") {
    name = "Private landlord";
    chip = "Common";
    point1 =
      "Around two thirds of Conny rent cases are with private landlords.";
    point2 =
      "Checking your rent does not contact your landlord or commit you to anything.";
    point3 =
      "In practice, these cases have not caused retaliation issues for tenants.";
  } else {
    return null;
  }

  return (
    <details className="cn-card">
      <summary style={{ listStyle: "none", cursor: "pointer" }}>
        <div className="cn-top">
          <div className="cn-icon">
            <ShieldIcon />
          </div>
          <div
            className="cn-head"
            style={{ display: "flex", alignItems: "center", gap: 10 }}
          >
            <span style={{ flex: 1, overflowWrap: "anywhere" }}>{name}</span>
            <span className="cn-chip">{chip}</span>
          </div>
        </div>
      </summary>
      <div className="cn-body">
        <Bullet>{point1}</Bullet>
        <Bullet>{point2}</Bullet>
        <Bullet>{point3}</Bullet>
      </div>
    </details>
  );
}

function CheckLine({
  children,
  textClassName = "cn-bullet-text",
}: {
  children: React.ReactNode;
  textClassName?: string;
}) {
  return (
    <div className="cn-bullet">
      <span className="cn-row-left">
        <span className="cn-check">
          <CheckIcon />
        </span>
        <span className={textClassName}>{children}</span>
      </span>
    </div>
  );
}

function Bullet({ children }: { children: React.ReactNode }) {
  return <CheckLine>{children}</CheckLine>;
}
